import { useEffect } from "react";
import { useForm } from "react-hook-form";

import { Input, Radio, TextArea } from "components/form_components/Inputs";
import Button from "components/ui/button/Button";
import { RELEVANCY_SEARCH_NEW_USERS, useVariableValue } from "lib/featureFlags";
import { useAllViews } from "lib/hooks/api/notices/views/useAllViews";
import { SavedViewDataType } from "lib/types/graphQLEnums";

import css from "./SavedViewForm.module.scss";

export type FormValues = {
  viewName: string;
  viewDescription: string;
  alertFrequency: "DAILY" | "WEEKLY" | "NEVER";
};

export function SavedViewForm({
  defaultValues,
  isStandardView,
  onSubmit,
  onCancel,
  isLoading,
}: {
  defaultValues?: FormValues;
  isStandardView?: boolean;
  onSubmit: (data: FormValues) => void;
  onCancel: () => void;
  isLoading?: boolean;
}) {
  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues,
  });

  const isRelevancySearchNewUsers = useVariableValue(RELEVANCY_SEARCH_NEW_USERS, false);

  const { data: allViews } = useAllViews(SavedViewDataType.Notice);

  useEffect(() => {
    // Can't directly set defaultValues since the object may be recreated while containing identical values
    // So instead comparing the individual fields which are primitives
    reset({
      viewName: defaultValues?.viewName,
      viewDescription: defaultValues?.viewDescription,
      alertFrequency: defaultValues?.alertFrequency,
    });
  }, [
    reset,
    defaultValues?.viewName,
    defaultValues?.viewDescription,
    defaultValues?.alertFrequency,
  ]);

  return (
    <form className={css.form} onSubmit={handleSubmit((d) => onSubmit(d))}>
      <Input
        control={control}
        label="Name"
        placeholder="Type search name here..."
        name="viewName"
        type="text"
        disabled={isStandardView}
        tooltipText={isStandardView ? "You cannot edit the name of a standard search" : ""}
        rules={{
          required: {
            value: true,
            message: "Search name is required",
          },
          validate: (val) => {
            if (val && defaultValues?.viewName === val) {
              return true;
            }

            if (val && allViews?.map((v) => v.name.toLowerCase()).includes(val.toLowerCase())) {
              return "A search already exists with this name";
            }
            return true;
          },
        }}
      />

      {!isRelevancySearchNewUsers && (
        <TextArea
          control={control}
          label="Description"
          placeholder="What does this search show? Enter a description here..."
          name="viewDescription"
          disabled={isStandardView}
          tooltipText={isStandardView ? "You cannot edit the description of a standard search" : ""}
        />
      )}

      {defaultValues?.alertFrequency && (
        <Radio
          label="Email me about new activity in this search"
          name={"alertFrequency"}
          control={control}
          options={[
            { label: "Daily", value: "DAILY" },
            { label: "Weekly", value: "WEEKLY" },
            { label: "Never", value: "NEVER" },
          ]}
          optionType="default"
        />
      )}
      <div className={css.formActions}>
        <Button buttonType="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" loading={isLoading}>
          Save
        </Button>
      </div>
    </form>
  );
}
